import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";

import {
  Col,
  Row,
  Container,
} from "react-bootstrap";

import {
  Pagination,
  Empty,
} from "antd";

import { PosterGalleryService } from "../Services/PosterGalleryService";

import Swal from "sweetalert2";

import Loader from "../global/Loader";

import { useTranslation } from "react-i18next";

import loadingImg from "../assets/images/loading-img.gif";

import demoUser from "../assets/images/broken-img.jpg";


const PDFGallery = ({
  galleryItems = [],
  loadingTblData,
}) => {

  const { i18n } = useTranslation();

  const posterGalleryService = useMemo(
    () => new PosterGalleryService(),
    []
  );

  const [pageLoading, setPageLoading] =
    useState(false);

  const [currentPage, setCurrentPage] =
    useState(1);

  const [itemsPerPage, setItemsPerPage] =
    useState(9);


  const [imageCache, setImageCache] =
    useState({});


  const [lang, setLang] = useState(
    localStorage.getItem("language") === "en"
      ? 2
      : 1
  );


  /* ------------------------------------------------
     LANGUAGE
  ------------------------------------------------ */

  useEffect(() => {

    setLang(
      localStorage.getItem("language") === "en"
        ? 2
        : 1
    );

  }, [i18n.language]);


  /* ------------------------------------------------
     CURRENT PAGE DATA
  ------------------------------------------------ */

  const indexOfLastItem =
    currentPage * itemsPerPage;

  const indexOfFirstItem =
    indexOfLastItem - itemsPerPage;

  const currentImages =
    galleryItems.slice(
      indexOfFirstItem,
      indexOfLastItem
    );


  /* ------------------------------------------------
     DOWNLOAD IMAGE
  ------------------------------------------------ */

  const loadImage = useCallback(
    async (item) => {

      const imageId =
        item?.Img?.imgUniqueId;

      if (!imageId) {

        setImageCache((prev) => ({
          ...prev,
          [item.posterId]: demoUser,
        }));

        return;
      }


      /*
        Already downloaded?
        Then API call नहीं होगी.
      */

      if (imageCache[item.posterId]) {
        return;
      }


      /*
        पहले loading image दिखाएं
      */

      setImageCache((prev) => ({
        ...prev,
        [item.posterId]: loadingImg,
      }));


      try {

        const res =
          await posterGalleryService.ViewDocument(
            imageId
          );


        if (
          res?.status === 200 &&
          res?.data
        ) {

          const blob =
            new Blob(
              [res.data],
              {
                type:
                  item.Img.imgMimeType ||
                  "image/jpeg",
              }
            );


          const objectUrl =
            URL.createObjectURL(blob);


          setImageCache((prev) => ({
            ...prev,
            [item.posterId]: objectUrl,
          }));

        } else {

          setImageCache((prev) => ({
            ...prev,
            [item.posterId]: demoUser,
          }));

        }

      } catch (error) {

        console.error(
          "Error fetching poster image:",
          error
        );


        setImageCache((prev) => ({
          ...prev,
          [item.posterId]: demoUser,
        }));

      }

    },
    [
      posterGalleryService,
      imageCache,
    ]
  );


  /* ------------------------------------------------
     LOAD ONLY CURRENT PAGE IMAGES
  ------------------------------------------------ */

  useEffect(() => {

    if (!currentImages.length) {
      return;
    }


    currentImages.forEach((item) => {

      loadImage(item);

    });

  }, [
    currentPage,
    itemsPerPage,
    galleryItems,
    loadImage,
  ]);


  /* ------------------------------------------------
     CLEAN OBJECT URLS
  ------------------------------------------------ */

  useEffect(() => {

    return () => {

      Object.values(imageCache).forEach(
        (url) => {

          if (
            url &&
            url.startsWith("blob:")
          ) {

            URL.revokeObjectURL(url);

          }

        }
      );

    };

  }, []);


  /* ------------------------------------------------
     PAGE CHANGE
  ------------------------------------------------ */

  const handlePaginationChange =
    (page, pageSize) => {

      /*
        अगर page size बदला
        तो हमेशा first page पर जाएं
      */

      if (pageSize !== itemsPerPage) {

        setCurrentPage(1);

      } else {

        setCurrentPage(page);

      }

      setItemsPerPage(pageSize);

    };


  /* ------------------------------------------------
     VIEW POSTER PDF/DOCUMENT
  ------------------------------------------------ */

  const viewDoc = async (doc) => {

    try {

      setPageLoading(true);


      if (!doc?.docUniqueId) {

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text:
            lang === 1
              ? "पोस्टर दस्तावेज़ उपलब्ध नहीं है।"
              : "The poster document has not been uploaded!",
        });

        return;
      }


      const res =
        await posterGalleryService.ViewDocument(
          doc.docUniqueId
        );


      if (res?.status === 200) {

        const fileBlob =
          new Blob(
            [res.data],
            {
              type:
                doc.docMimeType ||
                "application/pdf",
            }
          );


        const dataUrl =
          window.URL.createObjectURL(
            fileBlob
          );


        window.open(
          dataUrl,
          "_blank",
          "noopener,noreferrer"
        );

      } else {

        throw new Error(
          "Document not found"
        );

      }

    } catch (error) {

      console.error(
        "Error fetching poster document:",
        error
      );


      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          lang === 1
            ? "पोस्टर दस्तावेज़ उपलब्ध नहीं है।"
            : "The poster document could not be loaded!",
      });

    } finally {

      setPageLoading(false);

    }

  };


  /* ------------------------------------------------
     EMPTY DATA
  ------------------------------------------------ */

  if (
    !loadingTblData &&
    galleryItems.length === 0
  ) {

    return (

      <Container fluid>

        <Row>

          <Col>

            <Empty
              description={
                lang === 1
                  ? (
                    <span
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      वर्तमान में, प्रदर्शित करने के
                      लिए कोई डेटा नहीं है ।
                    </span>
                  )
                  : (
                    <span
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      Currently, There are no data
                      to display.
                    </span>
                  )
              }
            />

          </Col>

        </Row>

      </Container>

    );

  }


  return (

    <Container fluid>

      {pageLoading && <Loader />}


      {/* ------------------------------------------
          GALLERY
      ------------------------------------------ */}

      <Row className="galleryWrap">

        {currentImages.map(
          (item, index) => {

            const imageUrl =
              imageCache[item.posterId] ||
              loadingImg;


            return (

              <Col
                lg={4}
                md={4}
                sm={6}
                xs={12}
                className="ant-image"
                key={
                  item.posterId ||
                  item.key ||
                  index
                }
              >

                <a
                  href="/"
                  onClick={(e) => {

                    e.preventDefault();

                    viewDoc(item.doc);

                  }}
                  className="pdfItem"
                >

                  <img
                    src={imageUrl}
                    alt={
                      item.posternotes ||
                      `Poster ${index + 1}`
                    }
                  />

                </a>

              </Col>

            );

          }
        )}

      </Row>


      {/* ------------------------------------------
          PAGINATION
      ------------------------------------------ */}

      {galleryItems.length > 0 && (

        <Row justify="center">

          <Col>

            <div
              className="img-pagination"
              style={{
                paddingBottom: "20px",
              }}
            >

              <Pagination
                current={currentPage}
                pageSize={itemsPerPage}
                total={galleryItems.length}
                onChange={
                  handlePaginationChange
                }
                showSizeChanger
                pageSizeOptions={[
                  "9",
                  "18",
                  "36",
                  "72",
                ]}
              />

            </div>

          </Col>

        </Row>

      )}

    </Container>

  );

};


export default PDFGallery;